// Regex-pattern input for the OCR text detector. Renders only when the
// active format is 'text'. Validates on every keystroke via `new RegExp`;
// invalid patterns keep the last valid value flowing to the decoder and
// mark the input in an error state instead of throwing on the rVFC tick.

import { useEffect, useRef, useState } from 'react';

interface TextPatternInputProps {
  /** Current committed pattern string. Should be the last valid one. */
  value: string;
  /** Called with each keystroke that parses to a valid RegExp. */
  onChange: (nextValid: string) => void;
}

const MAX_PATTERN_LENGTH = 128;

export function TextPatternInput({ value, onChange }: TextPatternInputProps) {
  const [draft, setDraft] = useState<string>(value);
  const [error, setError] = useState<string | null>(null);
  // Track what we last committed via onChange, so an incoming `value`
  // that matches our last committed value doesn't stomp an invalid draft
  // that hasn't yet parsed. This closes the fragile race where 4 Hz FPS
  // HUD re-renders would flush user drafts.
  const lastCommittedRef = useRef<string>(value);

  useEffect(() => {
    if (value !== lastCommittedRef.current) {
      setDraft(value);
      lastCommittedRef.current = value;
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setDraft(next);
    if (next.length > MAX_PATTERN_LENGTH) {
      setError(`Pattern too long (${next.length} > ${MAX_PATTERN_LENGTH})`);
      return;
    }
    try {
      // eslint-disable-next-line no-new
      new RegExp(next.trim());
      setError(null);
      lastCommittedRef.current = next;
      onChange(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid pattern');
    }
  };

  const errorId = 'text-pattern-input-error';
  return (
    <label
      className="flex items-center gap-2 text-sm text-gray-300"
      htmlFor="text-pattern-input"
    >
      <span>Text pattern</span>
      <input
        id="text-pattern-input"
        data-testid="text-pattern-input"
        type="text"
        value={draft}
        onChange={handleChange}
        spellCheck={false}
        autoComplete="off"
        className={[
          'w-40 rounded border bg-gray-800 px-2 py-1 font-mono text-sm text-gray-100',
          error ? 'border-red-500' : 'border-gray-600',
        ].join(' ')}
        aria-invalid={error !== null}
        aria-describedby={error ? errorId : undefined}
      />
      {error !== null && (
        <span
          id={errorId}
          role="alert"
          className="text-xs text-red-400"
          data-testid="text-pattern-input-error"
        >
          {error}
        </span>
      )}
    </label>
  );
}
